"use client";

import Image from "next/image";
import { useState } from "react";
import { FaPhoneAlt, FaFax, FaFilePrescription, FaClipboardList, FaFileInvoiceDollar, FaShippingFast, FaEnvelope } from "react-icons/fa";
import PhoneLink from "@/components/PhoneLink";
import HomeSearch from "./HomeSearch";

function ChevronDown() {
  return (
    <svg
      className="w-3.5 h-3.5 ml-0.5 opacity-50 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SocialIcon({ href, label, d }) {
  return (
    <a
      href={href}
      aria-label={label}
      className="inline-flex items-center justify-center w-11 h-11 text-slate-400 hover:text-emerald-600 transition-colors rounded"
    >
      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d={d} />
      </svg>
    </a>
  );
}

const humanLinks = [
  ["Antifungal", "/human-medicine/antifungal/"],
  ["Antiparasitic", "/human-medicine/antiparasitic/"],
  ["Autoimmune", "/human-medicine/autoimmune/"],
  ["Hair Loss", "/human-medicine/hair-loss/"],
  ["Hormone Replacement Therapy", "/human-medicine/hormone-replacement-therapy/"],
  ["Low Dose Naltrexone", "/human-medicine/low-dose-naltrexone/"],
  ["Methylene Blue", "/human-medicine/methylene-blue/"],
  ["Pain Management", "/human-medicine/pain-management/"],
  ["Peptides", "/human-medicine/peptides/"],
  ["Sexual Health", "/human-medicine/sexual-health/"],
  ["Skin Care", "/human-medicine/skin-care/"],
  ["Thyroid", "/human-medicine/thyroid/"],
  ["Women's Health", "/human-medicine/womens-health/"],
];

const vetLinks = [
  ["Overview", "/veterinary-medicine/"],
  ["Cardiology / Heart / Blood Pressure", "/veterinary-medicine/cardiology-heart-blood-pressure/"],
  ["Digestive", "/veterinary-medicine/digestive/"],
  ["Diuretics", "/veterinary-medicine/diuretics/"],
  ["Hormonal Endocrine", "/veterinary-medicine/hormonal-endocrine/"],
  ["Pain Medication", "/veterinary-medicine/pain-medication/"],
  ["Seizure Disorder", "/veterinary-medicine/seizure-disorder/"],
];

const DROPDOWN_OUTER = "absolute top-full left-0 z-50 pt-2 min-w-[210px]";
const DROPDOWN_INNER = "bg-white rounded-xl border border-slate-100 shadow-xl py-1.5";
const DROPDOWN_ITEM_CLS =
  "block px-4 py-2.5 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors";

export default function Header({ products = [] }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [desktopHumanOpen, setDesktopHumanOpen] = useState(false);
  const [mobileHumanOpen, setMobileHumanOpen] = useState(false);
  const [mobileVetOpen, setMobileVetOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="sticky top-0 z-50 bg-white">

      {/* ── Utility bar ── */}
      <div className="border-b border-slate-100 bg-slate-50">
        <div className="container mx-auto px-4 flex items-center justify-between h-9 text-xs">

          {/* Left: contact */}
          <div className="flex items-center gap-4 text-slate-500">
            <PhoneLink
              href="tel:18448227379"
              className="inline-flex items-center gap-1.5 hover:text-emerald-700 transition-colors font-medium"
              aria-label="Call 844-822-7379"
            >
              <FaPhoneAlt className="w-3 h-3" aria-hidden="true" />
              <span>844.822.7379</span>
            </PhoneLink>
            <span className="hidden md:inline text-slate-300" aria-hidden="true">|</span>
            <PhoneLink
              href="fax:18007861405"
              className="hidden md:inline-flex items-center gap-1.5 hover:text-emerald-700 transition-colors"
              aria-label="Fax 800-786-1405"
            >
              <FaFax className="w-3 h-3" aria-hidden="true" />
              <span>Fax: 800.786.1405</span>
            </PhoneLink>
          </div>

          {/* Right: social + quick links */}
          <div className="flex items-center gap-2">
            <div className="hidden lg:flex items-center">
              <SocialIcon
                href="https://www.facebook.com/cfspharmacy/"
                label="Follow us on Facebook"
                d="M22 12.07C22 6.53 17.52 2 12 2S2 6.53 2 12.07c0 5.03 3.66 9.2 8.44 9.88v-6.99h-2.54v-2.89h2.54v-2.2c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.89h-2.34v6.99C18.34 21.27 22 17.1 22 12.07z"
              />
              <SocialIcon
                href="https://www.instagram.com/cfspharmacy/"
                label="Follow us on Instagram"
                d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
              />
              <SocialIcon
                href="https://www.linkedin.com/company/care-first-specialty-pharmacy"
                label="Follow us on LinkedIn"
                d="M4.98 3.5c0 1.38-1.12 2.5-2.5 2.5S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM0 8h5v16H0V8zm7.5 0h4.78v2.19h.07c.66-1.25 2.28-2.56 4.69-2.56 5.02 0 5.95 3.3 5.95 7.59V24h-5v-7.41c0-1.77-.03-4.06-2.48-4.06-2.48 0-2.86 1.93-2.86 3.93V24h-5V8z"
              />
            </div>
            <span className="hidden lg:inline text-slate-200 mx-1" aria-hidden="true">|</span>
            <a
              href="/refill-rx/"
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors font-medium"
            >
              <FaFilePrescription className="w-3 h-3" aria-hidden="true" />
              Refill Rx
            </a>
            <a
              href="/contact-us/"
              className="inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors font-medium"
            >
              <FaEnvelope className="w-3 h-3" aria-hidden="true" />
              Contact Us
            </a>
            <a
              href="/how-to-order/"
              className="hidden md:inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors font-medium"
            >
              <FaClipboardList className="w-3 h-3" aria-hidden="true" />
              How to Order
            </a>
            <a
              href="/shipping-info/"
              className="hidden md:inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors font-medium"
            >
              <FaShippingFast className="w-3 h-3" aria-hidden="true" />
              Shipping
            </a>
            <a
              href="/get-a-quote/"
              className="hidden md:inline-flex items-center gap-1.5 text-slate-500 hover:text-emerald-700 transition-colors font-medium"
            >
              <FaFileInvoiceDollar className="w-3 h-3" aria-hidden="true" />
              Get a Quote
            </a>
          </div>
        </div>
      </div>

      {/* ── Main navigation bar ── */}
      <div className="border-b border-slate-100 shadow-sm bg-white">
        <div className="container mx-auto px-4 flex items-center justify-between h-[60px] xl:h-[68px]">

          {/* Logo */}
          <a href="/" title="CareFirst Specialty Pharmacy – Home">
            <Image
              src="/images/desktop-logo.jpg"
              alt="CareFirst Pharmacy Logo"
              width={280}
              height={80}
              priority
              sizes="(max-width: 640px) 200px, 280px"
              className="h-[48px] lg:h-[54px] xl:h-[70px] w-auto"
            />
          </a>

          {/* Desktop navigation */}
          <nav
            className="hidden lg:flex items-center gap-0.5 lg:text-xs xl:text-sm font-medium text-slate-700"
            aria-label="Main navigation"
          >
            {/* Human Medicine — state-driven hover for reliable dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDesktopHumanOpen(true)}
              onMouseLeave={() => setDesktopHumanOpen(false)}
            >
              <a
                href="/human-medicine/"
                className="inline-flex items-center gap-0.5 lg:px-2 xl:px-3 py-2 rounded-md hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                Human Medicine <ChevronDown />
              </a>
              {desktopHumanOpen && (
                <div className={DROPDOWN_OUTER}>
                  <div className={DROPDOWN_INNER}>
                    {humanLinks.map(([label, href]) => (
                      <a key={href} href={href} className={DROPDOWN_ITEM_CLS}>{label}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Veterinary — CSS group-hover */}
            <div className="relative group">
              <a
                href="/veterinary-medicine/"
                className="inline-flex items-center gap-0.5 lg:px-2 xl:px-3 py-2 rounded-md hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                Veterinary <ChevronDown />
              </a>
              <div className={`hidden group-hover:block ${DROPDOWN_OUTER}`}>
                <div className={DROPDOWN_INNER}>
                  {vetLinks.map(([label, href]) => (
                    <a key={href} href={href} className={DROPDOWN_ITEM_CLS}>{label}</a>
                  ))}
                </div>
              </div>
            </div>

            <a href="/prescribers/" className="lg:px-2 xl:px-3 py-2 rounded-md hover:text-emerald-700 hover:bg-emerald-50 transition-colors">
              Prescribers
            </a>

            <a href="/blog/" className="lg:px-2 xl:px-3 py-2 rounded-md hover:text-emerald-700 hover:bg-emerald-50 transition-colors">
              Blog
            </a>

            <a href="/contact-us/" className="lg:px-2 xl:px-3 py-2 rounded-md hover:text-emerald-700 hover:bg-emerald-50 transition-colors">
              Contact Us
            </a>
          </nav>

          {/* Search + mobile menu toggle */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:block">
              <HomeSearch inline products={products} />
            </div>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white font-semibold text-sm hover:bg-emerald-800 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-site-menu"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
              <span>{menuOpen ? "Close" : "Menu"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeMenu}
            aria-hidden="true"
          />

          {/* Panel */}
          <div
            id="mobile-site-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="relative w-[85%] max-w-sm bg-white h-full z-50 animate-slideIn flex flex-col shadow-2xl border-r border-slate-100"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <a href="/" onClick={closeMenu}>
                <Image
                  src="/images/desktop-logo.jpg"
                  alt="CareFirst"
                  width={180}
                  height={52}
                  className="h-10 w-auto"
                />
              </a>
              <button
                onClick={closeMenu}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Mobile search */}
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <HomeSearch products={products} onSelect={closeMenu} />
            </div>

            {/* Nav links */}
            <nav className="flex flex-col overflow-y-auto flex-1 divide-y divide-slate-100">
              {[
                ["Home", "/"],
                ["How to Order", "/how-to-order/"],
                ["Refill Rx", "/refill-rx/"],
                ["Get a Quote", "/get-a-quote/"],
                ["Where We Ship", "/shipping-info/"],
                ["Blog", "/blog/"],
              ].map(([name, href]) => (
                <a
                  key={name}
                  href={href}
                  className="block px-5 py-4 text-slate-700 font-medium hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                  onClick={closeMenu}
                >
                  {name}
                </a>
              ))}

              {/* Human Medicine accordion */}
              <div>
                <div className="flex items-center justify-between">
                  <a
                    href="/human-medicine/"
                    className="flex-1 block px-5 py-4 text-slate-700 font-medium hover:text-emerald-700 transition-colors"
                    onClick={closeMenu}
                  >
                    Human Medicine
                  </a>
                  <button
                    onClick={() => setMobileHumanOpen(!mobileHumanOpen)}
                    className="px-4 py-4 text-slate-400 hover:text-emerald-700 transition-colors"
                    aria-label="Toggle Human Medicine"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${mobileHumanOpen ? "rotate-180" : ""}`}
                      fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {mobileHumanOpen && (
                  <div className="bg-slate-50 divide-y divide-slate-100">
                    {humanLinks.map(([label, href]) => (
                      <a
                        key={href}
                        href={href}
                        className="block px-8 py-3 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                        onClick={closeMenu}
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Veterinary accordion */}
              <div>
                <div className="flex items-center justify-between">
                  <a
                    href="/veterinary-medicine/"
                    className="flex-1 block px-5 py-4 text-slate-700 font-medium hover:text-emerald-700 transition-colors"
                    onClick={closeMenu}
                  >
                    Veterinary Medicine
                  </a>
                  <button
                    onClick={() => setMobileVetOpen(!mobileVetOpen)}
                    className="px-4 py-4 text-slate-400 hover:text-emerald-700 transition-colors"
                    aria-label="Toggle Veterinary Medicine"
                  >
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${mobileVetOpen ? "rotate-180" : ""}`}
                      fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {mobileVetOpen && (
                  <div className="bg-slate-50 divide-y divide-slate-100">
                    {vetLinks.map(([label, href]) => (
                      <a
                        key={href}
                        href={href}
                        className="block px-8 py-3 text-sm text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                        onClick={closeMenu}
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="/prescribers/"
                className="block px-5 py-4 text-slate-700 font-medium hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                onClick={closeMenu}
              >
                Prescribers
              </a>

              <a
                href="/contact-us/"
                className="block px-5 py-4 text-slate-700 font-medium hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                onClick={closeMenu}
              >
                Contact Us
              </a>
            </nav>

            {/* Bottom strip — phone only */}
            <div className="px-5 py-4 border-t border-slate-100 bg-white flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <FaPhoneAlt className="w-3 h-3" aria-hidden="true" />
              <PhoneLink href="tel:18448227379" className="hover:text-emerald-700 transition-colors">
                844.822.7379
              </PhoneLink>
            </div>
          </div>
        </div>
      )}

    </header>
  );
}
